const { fuzzySearch } = require("./levenshtein-search");

function getMaxEditDistance(phrase) {
  if (phrase.length < 10) {
    return Math.ceil(phrase.length / 3);
  } else if (phrase.length < 50) {
    return Math.ceil(phrase.length / 10);
  } else {
    return Math.max(Math.ceil(phrase.length / 10), 8);
  }
}

/** 
 * find the phrase in the text with levenshtein search. Also normalizes match to the end of the word
 * @param {string} phrase - text to search for
 * @param {string} text - text in which to search
 * @param {boolean} multi - return multiple occurences
 * @returns {{start: number, end: number}[]} - matched sections to highlight
 **/
function searchText(phrase, text, { multi }) {
  const maxMaxDist = getMaxEditDistance(phrase);
  let maxDist = Math.ceil(maxMaxDist / 4); // start with a smaller edit distance. Much faster
  const matches = []
  while (maxDist <= maxMaxDist && !matches.length) {
    // TODO - speed up, find exact match first
    const phraseMatches = [...fuzzySearch(phrase, text, maxDist)]; // <-- heavy lifting here
    let bestMatches = phraseMatches
      .filter(x => {
        // make sure it doesn't start in the middle of a world
        const prevChar = text[x.start - 1];
        return !prevChar || !prevChar.match(/\w/);
      })
      .sort((a, b) => a.dist - b.dist);
    
    // take the best matches according to multi paramater
    if (bestMatches.length) {
      bestMatches = multi
        ? bestMatches.filter(x => x.dist === bestMatches[0].dist)
        : bestMatches.slice(0, 1);
      
      // and adjust match end index to contain a whole word
      const adjustedMatches = bestMatches.map(({ start, end }) => {
        let endIndex = end - 1;
        if (text[endIndex].match(/\w/)) {
          // extend match to the end of a word (find the first non-word character after end)
          let adjustedEnd;
          for (let i = endIndex + 1; i < text.length; i++) {
            if (!text[i].match(/\w/)) {
              adjustedEnd = i - 1; 
              break;
            }
          }
          endIndex = adjustedEnd || text.length - 1;
        }
        return { start, end: endIndex }
      });
      matches.push(...adjustedMatches);
    } else {
      console.warn("no matches for " + phrase + " in " + text);
      maxDist *= 2; // retry
    }
  }
  return matches
}

/**
 * use levenshtein search for Markup component
 * @param {string} text - text to search
 * @param {{phrase: string, markupStyling: any, markupClassName: any, multi?: boolean}[]} searchPhrases
 * 
 */
export function getAnnotatedText(text, searchPhrases) {
  // use levenshtein search to find search phrases and highlight
  const matches = [];
  for (const {
    phrase,
    markupClassName,
    markupStyling,
    multi
  } of searchPhrases) {
    const matchIndices = searchText(phrase.toLowerCase(), text.toLowerCase(), { multi });
    matches.push(...matchIndices.map(match => ({ ...match, markupClassName, markupStyling })));
  }
  // split text between highlights and not-highlighted
  // Also splits intersecting phrases (fairly naive. Not really built for nested highlights)
  const annotations = [];
  matches.sort((a, b) => a.start - b.start);
  function pushSeparator({
    start,
    end,
    markupClassName = null,
    markupStyling = null
  }) {
    if (start <= end) {
      annotations.push({
        term: text.slice(start, end + 1),
        start,
        end,
        markupClassName,
        markupStyling
      });
    }
  }
  while (matches.length) {
    const thisMatch = matches.shift();
    const lastMatch = annotations.length && annotations[annotations.length - 1];
    if (lastMatch && lastMatch.end >= thisMatch.start) {
      thisMatch.start = lastMatch.end + 1;
    }
    pushSeparator({
      start: lastMatch ? lastMatch.end + 1 : 0,
      end: thisMatch.start - 1
    });
    pushSeparator(thisMatch);
  }
  const lastMatch = annotations.length && annotations[annotations.length - 1];
  pushSeparator({
    start: lastMatch ? lastMatch.end + 1 : 0,
    end: text.length - 1
  });
  return annotations;
}
